// A stalled connection can leave a request open for minutes without failing,
// so a part that sends nothing for this long is abandoned and can be retried.
// It is measured between progress events, so slow uploads still finish.
export const UPLOAD_STALL_TIMEOUT_MS = 30_000;

export async function uploadChunkPart({
  chunk,
  uploadUrl,
  checksumSHA256,
  onProgress,
  abortSignal,
}: {
  chunk: Blob;
  uploadUrl: string;
  checksumSHA256: string;
  onProgress?: (progress: number) => void;
  abortSignal?: AbortSignal;
}): Promise<{ etag: string }> {
  const url = uploadUrl.startsWith('https') ? uploadUrl : uploadUrl.replace(/^http/, 'https');

  return new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new Error('Upload aborted'));
      return;
    }

    const xhr = new XMLHttpRequest();
    let hasStalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const restartStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        hasStalled = true;
        xhr.abort();
      }, UPLOAD_STALL_TIMEOUT_MS);
    };

    xhr.upload.addEventListener('progress', (event) => {
      restartStallTimer();
      if (onProgress && event.lengthComputable) {
        onProgress((event.loaded / event.total) * 100);
      }
    });

    xhr.addEventListener('loadend', () => {
      clearTimeout(stallTimer);
      if (xhr.readyState === 4 && xhr.status === 200) {
        const etag = xhr.getResponseHeader('ETag')?.replaceAll('"', '');
        if (!etag) {
          reject(new Error('No ETag returned'));
          return;
        }
        resolve({ etag });
      } else if (xhr.readyState === 4) {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error(hasStalled ? 'Upload stalled' : 'Upload aborted'));
    });

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => xhr.abort());
    }

    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('x-amz-checksum-sha256', checksumSHA256);
    xhr.send(chunk);
    restartStallTimer();
  });
}
